import * as React from "react";
import { IInputFieldProps, SelectFieldOption, SelectFieldSchema } from "@generation/core";
import { useCombobox } from "downshift";
import "./DownshiftSelectField.scss";
import { translate } from "../Locales/translator";

interface DownshiftSelectFieldProps extends IInputFieldProps<SelectFieldSchema> {
  value?: any;
  onChange?: (event: any) => void;
  options: SelectFieldOption[];
  className: string;
  isLoading: boolean;
}

export const DownshiftSelectField = (props: DownshiftSelectFieldProps) => {
  const [items, setItems] = React.useState(props.options);
  const isSettingItemsRef = React.useRef(false);
  const downshiftContainerRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (props.options) {
      setItems(props.options);
    }
  }, [props.options, props.isLoading]);

  function getItemFilter(inputValue) {
    const normalize = (value: string) =>
      value
        .toLocaleLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");

    const normalizedInputValue = normalize(inputValue);
    return function itemFilter(item) {
      return !inputValue || normalize(item.label).includes(normalizedInputValue);
    };
  }

  function getItemFilterByValue(inputValue) {
    return function itemFilter(item) {
      return item.value === inputValue;
    };
  }

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
    setHighlightedIndex,
    toggleMenu
  } = useCombobox({
    items,
    onInputValueChange({ inputValue }) {
      const isSettingItems = isSettingItemsRef.current;
      let itemByValue = props.value && props.options.find(getItemFilterByValue(props.value));

      if (
        (inputValue && itemByValue && inputValue !== itemByValue.label && !isSettingItems) ||
        (!props.value && inputValue && !isSettingItems)
      ) {
        const filtered = props.options.filter(getItemFilter(inputValue));
        if (filtered && filtered.length > 0) {
          setItems(filtered);
        } else {
          // needed to close a menu when there are no items (unknown value entered in input)
          toggleMenu();
        }
      }
      if (isSettingItems) {
        setItems(props.options);
        isSettingItemsRef.current = false;
      }
    },
    itemToString: (item) => (item ? item.label : ""),
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      if (
        props.onChange &&
        newSelectedItem &&
        props.options.find(getItemFilterByValue(newSelectedItem.value))
      ) {
        props.onChange(newSelectedItem?.value);
      }
      setItems(props.options);
      isSettingItemsRef.current = true;
    }
  });

  React.useLayoutEffect(() => {
    if (isOpen && downshiftContainerRef.current) {
      const list = downshiftContainerRef.current.querySelector(
        ".downshift-select__list"
      ) as unknown as HTMLElement;

      if (list) {
        if (items.length >= 4) {
          const maxOptionsHeight = 300;
          const bodyBoundingRect = document.body.getBoundingClientRect();
          const bottom = bodyBoundingRect.height;
          let listBoundingRect = list.getBoundingClientRect();
          if (listBoundingRect.top + window.scrollY + list?.scrollHeight > bottom) {
            let maxHeight =
              list?.scrollHeight - (listBoundingRect.top + window.scrollY + list?.scrollHeight - bottom);
            if (maxHeight > maxOptionsHeight) {
              maxHeight = maxOptionsHeight;
            }
            list.style.maxHeight = `${maxHeight}px`;
          } else {
            if (list.scrollHeight > maxOptionsHeight) {
              list.style.maxHeight = `${maxOptionsHeight}px`;
            } else {
              list.style.maxHeight = "none";
            }
          }
        }
      }
    }
  }, [isOpen, items]);

  React.useEffect(() => {
    if (props.value !== undefined) {
      const filtered = props.options.find(getItemFilterByValue(props.value));
      if (filtered) {
        setInputValue(filtered.label);
      } else {
        setInputValue(props.value);
      }
    }
  }, [props.value, props.options]);

  return (
    <>
      <div ref={downshiftContainerRef} className={`downshift-select ${props.className}`}>
        <input
          placeholder={props.field.placeholder ?? translate("DropdownSelecteerEenOptie")}
          className="downshift-select__input"
          {...getInputProps({
            onKeyDown: (event) => {
              switch (event.key) {
                case "Enter": {
                  event.preventDefault();

                  const filtered = props.options.find(getItemFilter(inputValue));
                  const filteredIndex = props.options.findIndex(getItemFilter(inputValue));
                  if (filtered && filteredIndex > -1 && props.onChange) {
                    if (highlightedIndex < 0) {
                      setInputValue(filtered.label);
                      props.onChange(filtered.value);
                      isSettingItemsRef.current = true;
                      setHighlightedIndex(filteredIndex);
                      setItems(props.options);
                    }
                  } else {
                    setInputValue("");
                    setItems(props.options);
                    if (props.onChange) {
                      props.onChange(undefined as unknown as any);
                    }
                  }

                  return false;
                }
                default:
                  break;
              }
            },
            onBlur: () => {
              const filtered = props.options.find(getItemFilter(inputValue));
              const filteredItem = filtered?.label.toLocaleLowerCase() === inputValue.toLocaleLowerCase();

              if (filteredItem) {
                setInputValue(filtered.label);
                props.onChange && props.onChange(filtered.value);
                isSettingItemsRef.current = true;
              } else {
                setItems(props.options);
                isSettingItemsRef.current = true;
                setInputValue("");
              }
            }
          })}
        />
        <button
          id={props.name}
          aria-label="toggle menu"
          className="downshift-select__button"
          type="button"
          {...getToggleButtonProps()}
        />
        <ul
          className={`downshift-select__list ${
            !(isOpen && items.length) && "downshift-select__list--hidden"
          }`}
          {...getMenuProps()}
        >
          {isOpen &&
            items.map((item, index) => (
              <li className={"downshift-select__option"} {...getItemProps({ key: item.value, index, item })}>
                <span title={item.label}>{item.label}</span>
              </li>
            ))}
        </ul>
      </div>
    </>
  );
};
